import { homedir } from 'node:os';
import path, { basename, dirname as getDirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import logger from '@clamjs/logger';

import { getGitOriginUrl } from './gitlab';
import type { NpmPackageJson } from './npm';
import { getJson } from './tools';

export interface PackageJson extends NpmPackageJson {
  tnpm: {
    mode: 'npm' | 'tnpm' | 'yarn';
    lockfile?: 'enable';
  };
  group: string;
  workspace?: string[];
}

export interface BuilderJson {
  assets: {
    builder: {
      name: string;
    };
  };
}

export interface TsconfigJson {
  compilerOptions: {
    paths: Record<string, string[]>;
  };
}

export const root = join(homedir(), '.clam/clamjs');

export const pwd = process.cwd();

export const filename = fileURLToPath(import.meta.url);

export const dirname = getDirname(filename);

/**
 * Clam源码 dirname
 *
 * Used for CLAMDEV=true mode only
 */
// export const clamjsDir = path.resolve(dirname, '../../../');

/**
 * Clam源码 packages dirname
 *
 * Used for CLAMDEV=true mode only
 */
export const clamjsPackagesDir = path.resolve(dirname, '../../');

export const folderName = basename(pwd);

export const getTscJson = (interrupt = true) => {
  return getJson<TsconfigJson>(join(pwd, 'tsconfig.json'), interrupt);
};

export const getPkgJson = (interrupt = true, cwd: string = pwd) => {
  return getJson<PackageJson>(join(cwd, 'package.json'), interrupt);
};

export const getAbcJson = (interrupt = true) => {
  return getJson<BuilderJson>(join(pwd, 'abc.json'), interrupt);
};

// TODO 待删除。存在两个问题：1）取到的是 utils 包的 package.json，cli 内的 version 出错，已改实现；2）此外，没有其他地方用到了
// export const getClamjsPkgJson = (interrupt = true) => {
//   return getJson<PackageJson>(join(dirname, '../package.json'), interrupt);
// };

export const edithRoot = join(homedir(), '.edith');

export const edithAccessToken = join(edithRoot, 'ACCESS_TOKEN');

// edith 环境变量
export const EDITH_ENV = 'EDITH_ENV';

export const getProcessEnv = (key: string): string => {
  return process.env[key] ?? '';
};

/** 获取项目 git 仓库信息。依次尝试 package.json 中的 "repository.url"、git 配置的 "remote.origin.url" */
export const getGitRepoInfo = (): {
  gitGroupName: string;
  gitRepoName: string;
  /** ’分组/仓库名‘, 即 `${gitGroupName}/${gitRepoName}` */
  repo: string;
  /** 仓库 git 地址，即 `repository.url` of package.json */
  repositoryUrl: string;
} => {
  const pkg = getPkgJson();
  const { repository } = pkg;
  const pkgRepoUrl = repository?.url;
  const gitOriginUrl = getGitOriginUrl();
  let repoUrl: string;
  if (pkgRepoUrl) {
    repoUrl = pkgRepoUrl;
    if (gitOriginUrl !== pkgRepoUrl) {
      logger.warn(`package.json 中的 "repository.url" 与 git 远程地址不一致，请检查!`);
    }
  } else if (gitOriginUrl) {
    repoUrl = gitOriginUrl;
    logger.warn(
      `package.json 中的 "repository.url" 不存在, 使用 git 配置 "remote.origin.url" 作为仓库地址`,
    );
  } else {
    throw new Error(
      `获取 git 仓库信息失败, package.json 中 "repository.url" 不存在, git 配置 "remote.origin.url" 也不存在`,
    );
  }

  // http 地址后缀补齐
  const repo = repoUrl.endsWith('.git') ? repoUrl : `${repoUrl}.git`;
  // https://gitlab.alibaba-inc.com/trip/rx-test-miniapp.git
  // git@gitlab.alibaba-inc.com:trip/rx-test-miniapp.git
  const names = repo.match(/([^/:]+\/[^/:]+)\.git$/u);
  if (names && names.length >= 2) {
    const [, groupRepoStr = ''] = names;
    const [gitGroupName = '', gitRepoName = ''] = groupRepoStr.split('/');
    return {
      gitGroupName,
      gitRepoName,
      repo: groupRepoStr,
      repositoryUrl: repoUrl,
    };
  }

  throw new Error(
    `获取 git 仓库信息失败, git 远程地址内容有误，请检查 repository.url 或 git remote.origin.url`,
  );
};
